import { startTransition } from 'react';
import { useConfiguration } from '../../../store/configurationStore';
import { LabelAutocompleteInputProps } from './types';
import { createFilterOptions, FilterOptionsState } from '@mui/material/useAutocomplete';

export const SELECT_ALL_VALUE = '__SELECT_ALL__';

export const useLabelAutocompleteInput = (
  props: LabelAutocompleteInputProps & { inputValue: string; onInputClear?: () => void }
) => {
  const { selectedColumn, labels, onAdd, onRemove, customSuggestions = [], showCheckboxes = false, showSelectAll = true, inputValue, onInputClear } = props;
  const { columnUniqueValues } = useConfiguration();

  // Get suggestions from column
  const columnSuggestions = selectedColumn
    ? columnUniqueValues[selectedColumn] || []
    : [];

  // Merge column suggestions with custom suggestions using Set for deduplication
  const allSuggestions = Array.from(
    new Set([...columnSuggestions, ...customSuggestions])
  );

  // Filter suggestions based on search input
  const filteredSuggestions = inputValue
    ? allSuggestions.filter(s =>
      s.toLowerCase().includes(inputValue.toLowerCase())
    )
    : allSuggestions;

  // Filter out selected labels only if showCheckboxes is false
  const suggestions = showCheckboxes
    ? filteredSuggestions
    : filteredSuggestions.filter(suggestion => !labels.some(label => label.value === suggestion));

  // Check if all filtered suggestions are selected
  const isAllSelected = showCheckboxes && filteredSuggestions.length > 0 &&
    filteredSuggestions.every(suggestion => labels.some(label => label.value === suggestion));

  const handleSelectAll = () => {
    // Use startTransition to batch all state updates
    startTransition(() => {
      if (isAllSelected) {
        // Deselect all - remove only filtered option-based labels
        labels.forEach(label => {
          if (filteredSuggestions.includes(label.value)) {
            onRemove(label.id);
          }
        });
      } else {
        // Select all - add missing filtered suggestions
        filteredSuggestions.forEach(suggestion => {
          if (!labels.some(label => label.value === suggestion)) {
            onAdd(suggestion);
          }
        });
      }
    });
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: string[]
  ) => {
    // If SELECT_ALL_VALUE was clicked, handle it specially and return
    if (newValue.includes(SELECT_ALL_VALUE)) {
      handleSelectAll();
      return;
    }

    const currentValues = labels.map(l => l.value);

    // Filter out SELECT_ALL_VALUE before processing (defensive)
    const filteredNewValue = newValue.filter(v => v !== SELECT_ALL_VALUE);
    const addedValues = filteredNewValue.filter(v => !currentValues.includes(v));
    addedValues.forEach(value => {
      if (value && value.trim()) {
        onAdd(value.trim());
      }
    });

    // Detect REMOVED values (also filter out SELECT_ALL_VALUE)
    const removedValues = currentValues.filter(
      v => !filteredNewValue.includes(v) && v !== SELECT_ALL_VALUE
    );
    removedValues.forEach(value => {
      const labelToRemove = labels.find(l => l.value === value);
      if (labelToRemove) {
        onRemove(labelToRemove.id);
      }
    });
  };

  // Create custom filter options that keeps SELECT_ALL always visible
  const filterOptions = createFilterOptions<string>();
  const customFilterOptions = (
    options: string[],
    state: FilterOptionsState<string>
  ) => {
    const filtered = filterOptions(options, state);

    // Always include SELECT_ALL_VALUE if it was in the original options
    if (
      options.includes(SELECT_ALL_VALUE) &&
      !filtered.includes(SELECT_ALL_VALUE)
    ) {
      return [SELECT_ALL_VALUE, ...filtered];
    }

    return filtered;
  };

  // Prepend SELECT_ALL_VALUE to options when needed
  const options =
    showCheckboxes && showSelectAll && suggestions.length > 0
      ? [SELECT_ALL_VALUE, ...suggestions]
      : suggestions;

  return {
    suggestions,
    handleChange,
    isAllSelected,
    handleSelectAll,
    customFilterOptions,
    options,
  };
};
